import { useEffect, useState, createContext } from "react";
import canonicalizeJSON from "canonicalize";
import deepEquals from "react-fast-compare";

import { useLoadProfileResourceWithParams } from "redux/features/profileResources";

/*

This module implements the capability for interaction between:

   * the top-level EditParticipation component, which loads the "participation
data" that contains all the information needed to set up the registration
form, such as what the various registration items are and what they cost, and

   * the components that render each item's section on the registration form,
which may need to alter the parameters that the top-level component uses to
load the participation data in order to provide item-specific qualifying data,
such as a registration fee discount code that, when provided, alters the fee
charged for that item.

To provide this interaction, this module:

   * Defines a context (participationStateContext) that will be provided by
the EditParticipation component and subscribed to by each item's component;

   * Provides a useLoadParticipation hook that the EditParticipation component
calls to load the participation. The hook maintains state for the parameters
used in the load, and returns an object with two entries:

     - apiState, the direct result of the load, just as the EditParticipation
       component would have eceived if it had directly loaded the participation
       data with useLoadProfileResource, and

     - participationState, an object that EditParticipation can provide
       to the context when rendering the tree of registration item
       components. Within this are
           + loadedData (a copy of "data" from apiState), 
           + previouslyLoadedData. a copy of loadedData from the previous
             render, so form elements can detect changes and take any
             necessary action,
           + paramState, an object with functions that can be used to access
             and alter the parameters used in the load (and to access the
             parameters that were in effect when the currently loaded data
             was requested, if different from the parameters for the 
             current request if that request hasn't completed yet).

(The EditParticipation component will do some additional augmentation of the
participationState object before providing it as the context, such as breaking
down changes between loadedData and previouslyLoadedData on a per-item basis,
and characterizing item changes based on whether or not they are simply
attributable to a change in fee-qualifying load parameters).

There is currently only a single parameter used (fee_qualifying_data) but more
could be added easily if needed.

Parameters are object-valued. For example, a discount code for the item whose
idenifying key string is itemKey aould be
        fee_qualifying_data[itemKey]["discount code"].
Parameter values are converted to a canonical JSON string when passed as a
URL parameter to the server.

The paramState methods allow for setting/fetching parameters using an array
of indices, starting with the parameter name. For example:

      paramState.getSubmitted(["fee_qualifying_data", itemKey, "discount code"])

returns loadParams.fee_qualifying_data[itemKey]["discount code"]

(where loadParams is the internal state managing the load parameters).

These are implemented with the general helper methods getNestedParamValue and
updateNestedParamValue.

*/

export const participationStateContext = createContext({});

function updateNestedParamValue(params, keys, newValue) {
  const [topKey, ...subKeys] = keys;
  const newParams = { ...params };
  const newCompositeValue =
    subKeys.length === 0
      ? newValue
      : updateNestedParamValue(params[topKey] ?? {}, subKeys, newValue);

  if (newCompositeValue === undefined) delete newParams[topKey];
  else newParams[topKey] = newCompositeValue;
  if (Object.keys(newParams) === 0) return undefined;
  else if (deepEquals(newParams, params)) return params;
  else return newParams;
}

function getNestedParamValue(params, keys) {
  const [topKey, ...subKeys] = keys;
  return subKeys.length === 0
    ? params[topKey]
    : getNestedParamValue(params[topKey] ?? {}, subKeys);
}

export function useLoadParticipation(personId, activityId) {
  const [loadParams, setLoadParams] = useState({});

  const [previouslyLoadedData, setPreviouslyLoadedData] = useState(null);

  const flatParams = Object.fromEntries(
    Object.entries(loadParams).map(([param, value]) => [
      param,
      value instanceof Object ? canonicalizeJSON(value) : value,
    ])
  );

  const apiState = useLoadProfileResourceWithParams(
    personId,
    "participations",
    personId + "/" + activityId,
    flatParams
  );

  const loadedData = apiState.data;
  useEffect(() => {
    if (!deepEquals(loadedData, previouslyLoadedData))
      setPreviouslyLoadedData(loadedData);
  }, [loadedData, previouslyLoadedData]);

  const paramState = {
    setSubmitted: (paramKeys, value) =>
      setLoadParams((params) =>
        updateNestedParamValue(params, paramKeys, value)
      ),
    getSubmitted: (paramKeys) => getNestedParamValue(loadParams, paramKeys),
    getEffective: (paramKeys) =>
      getNestedParamValue(apiState.params ?? {}, paramKeys),
  };

  return {
    apiState,
    participationState: {
      loadedData,
      previouslyLoadedData,
      paramState,
    },
  };
}
